import { Button, Grid, P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import { useFormikContext } from 'formik'
import get from 'lodash/get'

import store from 'state/create-store'
import { showModal } from 'state/actions'
import namespace from 'lib/formik/namespace'
import {
  PROBATE_ESTATE_ITEM_TYPES,
  ProbateEstateItem,
} from 'lib/models/probate-estate'
import EstateCard from 'components/estate-card'
import InputFloating from 'components/form/input-floating'
import CircularRadioGroup from 'components/form/circular-radio-group'
import NotesInput from 'components/notes-input'
import DeleteModal from 'routes/lead/probate-form/delete-modal'
import TextSpacer from 'routes/lead/probate-form/text-spacer'
import withForm from 'routes/lead/probate-form/with-form'
import { getItemsTotals } from 'routes/probate-estate/fact-find/get-totals'
import {
  PROBATE_LEAD_FORM_SECTIONS,
  PROBATE_LEAD_FORM_VALUES,
} from 'routes/lead/probate-form/constants'

import { HandleSaveProps, ItemProps } from './types'
import { useItems } from '../helpers/items-context'
import { useProbateEstate } from '../helpers/probate-estate-context'
import SubsectionHeading from '../subsection-heading'

const Item = ({ item, index, listLength }: ItemProps) => {
  const {
    DECEASED_SHARE_OF_JOINT_OWNERSHIP,
    DESCRIPTION,
    JOINT_OWNER_NAME,
    OWNERSHIP,
    VALUE,
    NOTES,
  } = PROBATE_LEAD_FORM_VALUES
  const { withNamespace, withoutNamespace } = namespace(
    `vehiclesAndPossessions.${item.id}.`
  )
  const { values } = useFormikContext()
  const { updateItem, deleteItem } = useItems()

  const handleSave = ({ name, value }: HandleSaveProps) => {
    updateItem(
      {
        id: item.id,
        attributes: { [withoutNamespace(name)]: value || null },
      },
      PROBATE_ESTATE_ITEM_TYPES.VEHICLE_OR_POSSESSION
    )
  }

  const onTrashClick = () => {
    store.dispatch(
      showModal({
        component: DeleteModal,
        headingText: 'Delete other',
        config: {
          deleteCallback: () => {
            deleteItem(item.id)
          },
          type: 'other',
          resourceId: item.id,
        },
      })
    )
  }

  return (
    <EstateCard
      onTrashClick={onTrashClick}
      listLength={listLength}
      listPosition={index + 1}
      key={item.id}
      type="other"
    >
      <Grid style={{ gridAutoFlow: 'row' }}>
        <Grid.Item span={9}>
          <InputFloating
            name={withNamespace(DESCRIPTION)}
            label="Description"
            handleSave={handleSave}
            hint="For vehicles, if we’re completing an IHT400 we’ll need reg number, age, mileage and condition"
          />
        </Grid.Item>
        <Grid.Item span={6}>
          <InputFloating
            name={withNamespace(VALUE)}
            label="Total value (£)"
            hint="For joint assets, the £ value of their share will be calculated automatically."
            handleSave={handleSave}
            isCurrency
          />
        </Grid.Item>
        <Grid.Item>
          <CircularRadioGroup
            name={withNamespace(OWNERSHIP)}
            label="Ownership"
            options={[
              { label: 'Not asked', value: '' },
              { label: 'Sole', value: 'sole' },
              { label: 'Joint', value: 'joint' },
            ]}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        {get(values, withNamespace(OWNERSHIP)) === 'joint' && (
          <>
            <Grid.Item span={6}>
              <InputFloating
                name={withNamespace(JOINT_OWNER_NAME)}
                label="Name of joint owner"
                handleSave={handleSave}
                hint="If more than one, leave a note below"
              />
            </Grid.Item>
            <Grid.Item span={6}>
              <InputFloating
                name={withNamespace(DECEASED_SHARE_OF_JOINT_OWNERSHIP)}
                label="Share owned by person who's died (%)"
                handleSave={handleSave}
              />
            </Grid.Item>
          </>
        )}
        <Grid.Item>
          <NotesInput name={withNamespace(NOTES)} handleSave={handleSave} />
        </Grid.Item>
      </Grid>
    </EstateCard>
  )
}

const ItemWithForm = withForm(Item)

const Other = ({ isComplete }: { isComplete: boolean }) => {
  const { VEHICLES_AND_POSESSIONS } = PROBATE_LEAD_FORM_SECTIONS
  const { HOUSEHOLD_POSSESSIONS_VALUE } = PROBATE_LEAD_FORM_VALUES
  const { values } = useFormikContext<{
    [HOUSEHOLD_POSSESSIONS_VALUE]: boolean
  }>()
  const { saveProbateEstateField } = useProbateEstate()
  const { groupedItems, createItem } = useItems()
  const { vehicleOrPossession: vehiclesOrPossessionsItems = [] } = groupedItems
  const toggleDisabled =
    !!values[HOUSEHOLD_POSSESSIONS_VALUE] ||
    vehiclesOrPossessionsItems.length > 0

  const itemsToTotal = [
    ...vehiclesOrPossessionsItems,
    {
      attributes: {
        value: values[HOUSEHOLD_POSSESSIONS_VALUE],
        ownership: 'sole',
      },
    },
  ] as ProbateEstateItem[]

  const { total, sole, joint, unspecified } = getItemsTotals(itemsToTotal)
  return (
    <SubsectionHeading
      disabled={toggleDisabled}
      name={VEHICLES_AND_POSESSIONS}
      onToggle={saveProbateEstateField}
      scrollId="6.8"
      title="Other"
      isComplete={isComplete}
    >
      <Grid.Item span={11}>
        <P size="S" data-testid="items-value-summary">
          Total: {total} <TextSpacer /> Sole: {sole} <TextSpacer /> Joint share:{' '}
          {joint} <TextSpacer /> Unspecified: {unspecified}
        </P>
        <Grid margin={['M', 0]}>
          <Grid.Item span={8}>
            <InputFloating
              name={HOUSEHOLD_POSSESSIONS_VALUE}
              label="Value of household possessions (£)"
              handleSave={saveProbateEstateField}
              hint="£1000 is typical"
              isCurrency
            />
          </Grid.Item>
        </Grid>

        <P>
          You can also add individual items below.
          <br />
          If there are specific, valuable items left in the will, add them
          below.
        </P>

        {vehiclesOrPossessionsItems.length > 0 && (
          <>
            {vehiclesOrPossessionsItems.map((item, index) => (
              <ItemWithForm
                enableReinitialize={false}
                index={index}
                initialValues={{
                  vehiclesAndPossessions: { [item.id]: item.attributes },
                }}
                item={item}
                key={item.id}
                listLength={vehiclesOrPossessionsItems.length}
              />
            ))}
          </>
        )}
      </Grid.Item>
      <Grid.Item>
        <Button.Secondary
          style={{ paddingTop: GTR.XXS, paddingBottom: GTR.XXS }}
          type="button"
          onClick={() => {
            createItem({
              itemType: PROBATE_ESTATE_ITEM_TYPES.VEHICLE_OR_POSSESSION,
            })
          }}
        >
          Add other
        </Button.Secondary>
      </Grid.Item>
    </SubsectionHeading>
  )
}

export default Other
